import { FC, useEffect, useMemo, useState } from 'react'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { CountdownEvent } from '@/components/CountdownEvent/CountdownEvent'
import { eventStatus } from '@/constants/common'
import { icons } from '@/constants/icons'
import { UnlockWithCreditButton } from '@/containers/EventDetailPage/Buttons/UnlockWithCreditButton'
import { useEventVideoSources } from '@/hooks/useEventVideoSources'
import { getCurrentLanguage } from '@/i18n'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { EventActionDisplayProps } from '@/types/componentsPropsTypes'
import { eventTypes } from '@/types/servicesTypes/configTypes'
import { checkIsChannelPlayable, checkIsValidDeepLink } from '@/utils/utils'

import { DeepLinkButton, LoginButton, WatchButton } from './Buttons'
import { EventInformation } from './EventInformation'
import { EventDetailInfo, EventDisplayWrapper } from './styles'

const formatEuro = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
})

export const EventActionDisplay: FC<EventActionDisplayProps> = ({
  event,
  eventChannel,
  setIsEventActionMounted,
  channelLogoSources,
  currentEventStatus,
  isRedeemablePPVEvent,
  parentFocusId,
  goToLogin,
}) => {
  const { t } = useCommonTranslation()
  const { isRegisteredUser } = useAuth()
  const { eventVideoSources, isEventActionMounted } =
    useEventVideoSources(event)
  const [isPlayableData, setIsPlayableData] = useState<Nullable<boolean>>(null)

  const { isAccessible, isPremium, isPPVEvent, isRequiresRegistrationOnly } =
    useMemo(() => {
      return getContentAccessFlags(event, null, isRegisteredUser)
    }, [event, isRegisteredUser])

  useEffect(() => {
    if (event && eventVideoSources !== undefined) {
      const isPlayableData = checkIsChannelPlayable(
        event,
        !!eventVideoSources,
        isAccessible,
      )
      setIsPlayableData(isPlayableData)
    }
    isEventActionMounted && setIsEventActionMounted(true)
  }, [event, eventVideoSources, isEventActionMounted, isAccessible])

  const isChannelEvent = event.type === eventTypes.CHANNEL

  const hasValidDeepLink = useMemo(() => checkIsValidDeepLink(event), [event])

  const isChannelWatchable = !!event?.watchable

  const isLinkable = isChannelWatchable && hasValidDeepLink

  const isLiveNow = currentEventStatus === eventStatus.IS_LIVE
  const isFutureEvent = currentEventStatus === eventStatus.IS_FUTURE
  const isPastEvent = currentEventStatus === eventStatus.IS_PAST
  const isPastChannelEvent = isPastEvent && isChannelEvent

  const canWatchInsideApp = !!isPlayableData && !isPastChannelEvent
  const canWatchViaDeeplink = isLinkable && !isPremium && !isPastEvent

  const canUnlockWithCredit =
    isPPVEvent && isRegisteredUser && isRedeemablePPVEvent

  const isFuturePremiumChannelEvent =
    isChannelEvent && (isPremium || isPPVEvent) && isFutureEvent

  const notAccessibleWithoutFocus =
    !isAccessible &&
    isFuturePremiumChannelEvent &&
    !canUnlockWithCredit &&
    isRegisteredUser

  const hasFocusNode =
    (canWatchInsideApp &&
      (isAccessible ? !isFutureEvent : !notAccessibleWithoutFocus)) ||
    (canWatchViaDeeplink && !isFutureEvent)

  const actionsWithoutEventInformation =
    canWatchInsideApp && isAccessible && !isFutureEvent && !isChannelEvent
  // debug
  const renderDebugInformation = () => {
    const onEnded =
      event.type === eventTypes.YOUTUBE
        ? 'close'
        : event.type === eventTypes.CHANNEL
        ? 'endedScreen'
        : event.video?.onEnded || 'loop'

    return (
      isDevMode && (
        <EventDetailInfo>
          eventType: <span>{event.type}</span>
          isPremiumEvent: <span>{isPremium.toString()}</span>
          isLiveEvent: <span>{(!!event.live).toString()}</span>
          eventTime: <span>{currentEventStatus}</span>
          isPlayable: <span> {(!!isPlayableData).toString()}</span>
          isLinkable: <span>{isLinkable.toString()}</span>
          onEnded: <span>{onEnded}</span>
        </EventDetailInfo>
      )
    )
  }
  // debug

  const renderPlayableActions = () => {
    if (isAccessible) {
      return isFutureEvent ? (
        <>
          <EventInformation
            sources={channelLogoSources}
            eventInformation={
              isPremium
                ? t('detailPage.information.futureAvailablePremium')
                : t('detailPage.information.waitCountdownEnds')
            }
          />
          <CountdownEvent startTime={event.startTime} />
        </>
      ) : (
        <>
          {isChannelEvent &&
            (isPremium ? (
              <EventInformation
                sources={channelLogoSources}
                eventInformation={t(
                  'detailPage.information.availablePremiumChannel',
                )}
              />
            ) : (
              <EventInformation
                sources={channelLogoSources}
                eventInformation={t('detailPage.information.freeChannel')}
              />
            ))}
          <WatchButton
            isLive={isLiveNow}
            event={event}
            eventVideoSources={eventVideoSources}
          />
        </>
      )
    }
    const language = getCurrentLanguage()
    const wallet =
      event.ppvInfo?.find(({ walletId }) =>
        walletId?.startsWith('promotion'),
      ) || event.ppvInfo?.[0]

    const ppvEventCostTypeNumber = wallet?.cost

    const walletName =
      wallet?.walletId.replace(/^promotion-/, '').replace(/-credits$/, '') || ''

    const ppvEventCostLocalized =
      ppvEventCostTypeNumber?.toLocaleString(language)

    const ppvEventCostTypeString = event.ppvInfo
      ? `${ppvEventCostLocalized ?? ''} ${walletName}`
      : ''

    const PPVEventInformation = t('detailPage.information.purchaseByCredits', {
      cost: ppvEventCostTypeString,
      count: ppvEventCostTypeNumber,
      euro: formatEuro.format(Number(ppvEventCostTypeNumber) / 100),
    })

    if (isChannelEvent && (isPremium || isPPVEvent) && isFutureEvent) {
      const eventInformation = isPPVEvent
        ? PPVEventInformation
        : t('detailPage.information.waitCountdownEndsPremium')

      const qrImage =
        isPPVEvent && isRegisteredUser && !canUnlockWithCredit
          ? icons.navigation.exit
          : ''

      return (
        <>
          <EventInformation
            sources={channelLogoSources}
            eventInformation={eventInformation}
            qrImage={qrImage}
          />

          {isRegisteredUser ? (
            <CountdownEvent startTime={event.startTime} />
          ) : (
            <LoginButton
              goToLogin={goToLogin}
              label={isRequiresRegistrationOnly ? 'loginAndWatch' : 'login'}
            />
          )}
          {canUnlockWithCredit && (
            <UnlockWithCreditButton
              cost={ppvEventCostTypeString}
              event={event}
              eventChannel={eventChannel}
              parentFocusId={parentFocusId}
            />
          )}
        </>
      )
    }

    const registerEventInformation = isPPVEvent
      ? PPVEventInformation
      : t('detailPage.information.needSubscription')

    const anonymousEventInformation = isPPVEvent
      ? PPVEventInformation
      : t('detailPage.information.loginToWatch')

    const eventInformation = isRegisteredUser
      ? registerEventInformation
      : anonymousEventInformation

    const qrImage =
      isPPVEvent && isRegisteredUser && !canUnlockWithCredit
        ? icons.navigation.exit
        : ''

    return (
      <>
        <EventInformation
          sources={channelLogoSources}
          eventInformation={eventInformation}
          qrImage={qrImage}
        />
        {isRegisteredUser ? (
          <UnlockWithCreditButton
            cost={ppvEventCostTypeString}
            event={event}
            eventChannel={eventChannel}
            parentFocusId={parentFocusId}
          />
        ) : (
          <LoginButton
            goToLogin={goToLogin}
            label={isRequiresRegistrationOnly ? 'loginAndWatch' : 'login'}
          />
        )}
      </>
    )
  }

  const renderLinkableActions = () => {
    const deepLinkData = event.deepLink

    const providerName = deepLinkData?.tizen?.appName || t('anotherApp')

    const eventInformation = t('detailPage.information.availableInApp', {
      providerName,
    })

    return (
      <>
        <EventInformation
          sources={channelLogoSources}
          eventInformation={eventInformation}
        />
        {isFutureEvent ? (
          <CountdownEvent startTime={event.startTime} />
        ) : (
          <DeepLinkButton
            deepLinkData={deepLinkData}
            categoryId={event.categoryId}
          />
        )}
      </>
    )
  }

  const renderActions = () => {
    if (canWatchInsideApp) {
      return renderPlayableActions()
    }

    if (canWatchViaDeeplink) {
      return renderLinkableActions()
    }

    if (isFutureEvent) {
      return (
        <>
          <EventInformation
            sources={channelLogoSources}
            eventInformation={t('detailPage.information.upcomingUnavailable')}
          />
          <CountdownEvent startTime={event.startTime} />
        </>
      )
    }

    const eventInformation = isPastEvent
      ? t('eventOver')
      : t('detailPage.information.liveUnavailable')

    return (
      <EventInformation
        sources={channelLogoSources}
        eventInformation={eventInformation}
      />
    )
  }

  return (
    <EventDisplayWrapper
      focusId="EventAction"
      disabled={!hasFocusNode}
      $alignContentEnd={actionsWithoutEventInformation}
    >
      {renderDebugInformation()}
      {isPlayableData !== null && renderActions()}
    </EventDisplayWrapper>
  )
}
